import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import WordMatchingGame from './WordMatchingGame'
import WordMeaningQuiz from './WordMeaningQuiz'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Alert,
  Divider
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Book as BookIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { doc, getDoc, addDoc, collection } from 'firebase/firestore'
import toast from 'react-hot-toast'

const WordTaskSolve = ({ taskId, taskTitle, taskDescription, taskMode, onBack }) => {
  const { user } = useAuth()
  const [taskData, setTaskData] = useState(null)
  const [words, setWords] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentView, setCurrentView] = useState('detail') // 'detail', 'matching-game', 'meaning-quiz'

  useEffect(() => {
    if (taskId) {
      loadWordTaskDetails()
    }
  }, [taskId])

  const loadWordTaskDetails = async () => {
    try {
      setLoading(true)
      
      const taskDoc = await getDoc(doc(db, 'wordTasks', taskId))
      
      if (taskDoc.exists()) {
        const data = taskDoc.data()
        setTaskData(data)
        
        const wordsData = data.words || []
        setWords(wordsData)
      } else {
        toast.error('Görev bulunamadı')
        onBack()
      }
    } catch (error) {
      console.error('Error loading word task:', error)
      toast.error('Görev detayları yüklenirken hata oluştu')
      onBack()
    } finally {
      setLoading(false)
    }
  }

  const handleStartTask = async () => {
    const currentTaskMode = taskData?.taskMode || taskMode || 'study'
    
    if (currentTaskMode === 'study') {
      // Kelimeleri çalış - Görevi tamamlandı olarak işaretle
      await completeStudyTask()
    } else if (currentTaskMode === 'matching') {
      // Eşleştirme oyunu başlat
      setCurrentView('matching-game')
    } else if (currentTaskMode === 'meaning') {
      // Anlam sorma testi başlat
      setCurrentView('meaning-quiz')
    }
  }

  const handleBackToDetail = () => {
    setCurrentView('detail')
    // Görev listesine dönüş için ana onBack'i çağır
    onBack()
  }

  const completeStudyTask = async () => {
    try {
      const studentId = user.uid
      
      // Önce word task'ın teacherId'sini al (Android uygulamasıyla aynı)
      const taskDoc = await getDoc(doc(db, 'wordTasks', taskId))
      
      if (taskDoc.exists()) {
        const taskData = taskDoc.data()
        const teacherId = taskData.teacherId
        const classId = taskData.classId
        const className = taskData.className
        
        const result = {
          wordTaskId: taskId,
          studentId: studentId,
          teacherId: teacherId,
          classId: classId,
          className: className,
          score: words.length, // Çalışma görevi için kelime sayısı kadar puan
          totalQuestions: words.length,
          completionDate: new Date(),
          taskMode: 'study'
        }
        
        await addDoc(collection(db, 'wordTaskResults'), result)
        
        toast.success('Kelime görevi başarıyla tamamlandı!')
        console.log('Kelime görevi sonucu kaydedildi:', result)
        onBack()
      }
    } catch (error) {
      console.error('Error completing study task:', error)
      toast.error('Görev tamamlanırken hata oluştu')
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Tarih belirtilmemiş'
    return timestamp.toDate().toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTaskModeLabel = (mode) => {
    switch (mode) {
      case 'study':
        return 'Kelimeleri Çalış'
      case 'matching':
        return 'Eşleştirme Oyununu Başlat'
      case 'meaning':
        return 'Anlam Sorma Testini Başlat'
      default:
        return 'Görevi Başlat'
    }
  }

  const getTaskModeDescription = (mode) => {
    switch (mode) {
      case 'study':
        return 'Kelimeleri inceleyin ve öğrenin'
      case 'matching':
        return 'Kelimeleri anlamlarıyla eşleştirin'
      case 'meaning':
        return 'Kelimelerin anlamlarını test edin'
      default:
        return 'Görev modunu başlatın'
    }
  }

  if (loading) {
    return (
      <SpaceTheme>
        <Box sx={{ flexGrow: 1 }}>
          <AppBar position="static" sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                {taskTitle || 'Kelime Görevi'}
              </Typography>
            </Toolbar>
          </AppBar>
          <Container maxWidth="md" sx={{ py: 3 }}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography>Kelime görevi yükleniyor...</Typography>
            </Box>
          </Container>
        </Box>
      </SpaceTheme>
    )
  }

  if (!taskData) {
    return (
      <SpaceTheme>
        <Box sx={{ flexGrow: 1 }}>
          <AppBar position="static" sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Görev Hatası
              </Typography>
            </Toolbar>
          </AppBar>
          <Container maxWidth="md" sx={{ py: 3 }}>
            <Alert severity="error">
              Kelime görevi yüklenemedi. Lütfen daha sonra tekrar deneyin.
            </Alert>
          </Container>
        </Box>
      </SpaceTheme>
    )
  }

  const currentTaskMode = taskData.taskMode || taskMode || 'study'

  // Eşleştirme oyunu sayfasını göster
  if (currentView === 'matching-game') {
    return (
      <WordMatchingGame
        taskId={taskId}
        taskTitle={taskData.title || taskTitle}
        onBack={handleBackToDetail}
      />
    )
  }

  // Anlam sorma quiz sayfasını göster
  if (currentView === 'meaning-quiz') {
    return (
      <WordMeaningQuiz
        taskId={taskId}
        taskTitle={taskData.title || taskTitle}
        onBack={handleBackToDetail}
      />
    )
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
        {/* App Bar */}
        <AppBar position="static" sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {taskData.title || taskTitle}
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="md" sx={{ py: 3 }}>
          {/* Task Info Card */}
          <Card sx={{ 
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}>
            <CardContent>
              <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                📚 {taskData.title || taskTitle}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {taskData.description || taskDescription}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <ScheduleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  Oluşturulma: {formatDate(taskData.createdDate)}
                </Typography>
                
                {taskData.dueDate && !taskData.unlimited && (
                  <Typography variant="body2" color="error.main">
                    ⏰ Son Tarih: {formatDate(taskData.dueDate)}
                  </Typography>
                )}
                
                {taskData.unlimited && (
                  <Typography variant="body2" color="success.main">
                    ♾️ Sınırsız Süre
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" color="primary">
                    {words.length} Kelime
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getTaskModeDescription(currentTaskMode)}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleStartTask}
                  startIcon={<PlayIcon />}
                  sx={{ minWidth: 200 }}
                >
                  {getTaskModeLabel(currentTaskMode)}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Words List Card */}
          <Card sx={{ 
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Kelime Listesi ({words.length})
              </Typography>
              
              {words.length > 0 ? (
                <List>
                  {words.map((word, index) => (
                    <React.Fragment key={index}>
                      <ListItem
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                        }}
                      >
                        <ListItemIcon>
                          <BookIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="h6" fontWeight="bold">
                              {word.english || word.word || 'Kelime'}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              {word.turkish || word.meaning || 'Anlam'}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < words.length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Alert severity="info">
                  Bu görevde henüz kelime bulunmuyor.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Instructions Card */}
          <Alert severity="info">
            <Typography variant="body2">
              💡 <strong>Bilgi:</strong> {currentTaskMode === 'study' 
                ? 'Kelimeleri inceledikten sonra "Kelimeleri Çalış" butonuna tıklayarak görevi tamamlayabilirsiniz.'
                : 'Görev modunu başlatmak için yukarıdaki butonu kullanın.'
              }
            </Typography>
          </Alert>
        </Container>
      </Box>
    </SpaceTheme>
  )
}

export default WordTaskSolve
